import React, { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import api from '../services/api';
import { getSmsCabinetUrl } from '../config/smsCabinet';

/**
 * При AUTH_COOKIE_USER_ID на бэкенде без cookie /auth/me → 401: не показываем конструктор.
 */
const RequireAuth: React.FC = () => {
  const { t } = useTranslation();
  const [state, setState] = useState<'loading' | 'ok' | 'no' | 'error'>('loading');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await api.getAuthMe();
        void api.recordConstructorVisit().catch(() => {
          /* визит — best-effort, не блокируем вход */
        });
        if (!cancelled) setState('ok');
      } catch (e) {
        const status = (e as AxiosError)?.response?.status;
        if (status === 401) {
          if (!cancelled) setState('no');
        } else if (!cancelled) {
          setState('error');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state === 'loading') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (state === 'no') {
    const cabinet = getSmsCabinetUrl();
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          p: 2,
          gap: 2,
          bgcolor: 'background.default',
        }}
      >
        <Typography variant="h6">
          {t('auth.requiredTitle', 'Нужна авторизация')}
        </Typography>
        <Typography color="text.secondary" align="center" sx={{ maxWidth: 420 }}>
          {t(
            'auth.requiredHint',
            'Войдите в личный кабинет SMS и откройте конструктор бота оттуда (или пройдите вход в конструктор).',
          )}
        </Typography>
        {cabinet && (
          <Button variant="contained" href={cabinet} component="a">
            {t('auth.goToCabinet', 'Перейти в кабинет')}
          </Button>
        )}
      </Box>
    );
  }

  if (state === 'error') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          p: 2,
          gap: 2,
        }}
      >
        <Typography color="error">
          {t('auth.checkFailed', 'Не удалось проверить авторизацию. Попробуйте обновить страницу.')}
        </Typography>
        <Button variant="outlined" onClick={() => window.location.reload()}>
          {t('common.retry', 'Обновить')}
        </Button>
      </Box>
    );
  }

  return <Outlet />;
};

export default RequireAuth;
